import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, Image } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import { sanitizeInput, escapeHtml, validateImageUrl } from '../../utils/sanitize';

const Gallery = ({ user }) => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    image: null
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const galleries = await apiService.getGalleries();
      setGalleries(galleries);
    } catch (error) {
      toast.error('Failed to fetch galleries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) data.append(key, formData[key]);
      });

      if (editingGallery) {
        await apiService.updateGallery(editingGallery.id, data);
      } else {
        await apiService.createGallery(data);
      }

      toast.success(`Gallery item ${editingGallery ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingGallery(null);
      setFormData({ title: '', description: '', category: '', image: null });
      fetchGalleries();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleView = (gallery) => {
    setSelectedGallery(gallery);
    setShowViewModal(true);
  };

  const handleEdit = (gallery) => {
    setEditingGallery(gallery);
    setFormData({
      title: gallery.title,
      description: gallery.description,
      category: gallery.category,
      image: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    toast((t) => (
      <div>
        <p>Are you sure you want to delete this gallery item?</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  apiService.setToken(token);
                }
                await apiService.deleteGallery(id);
                toast.success('Gallery item deleted successfully');
                fetchGalleries();
              } catch (error) {
                toast.error('Failed to delete gallery item');
              }
            }}
            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ background: '#6b7280', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    {
      key: 'image_url',
      label: 'Image',
      render: (gallery) => (
        gallery.image_url && validateImageUrl(gallery.image_url) ? (
          <img src={`${process.env.REACT_APP_API_URL_WITH_OUT_API}${gallery.image_url}`} crossOrigin='anonymous' alt={escapeHtml(gallery.title)} style={{ width: '140px' }} className="w-12 h-12 object-cover rounded" />
        ) : (
          <span className="text-gray-400">No image</span>
        )
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (gallery) => (
        gallery.description?.length > 50
          ? `${escapeHtml(gallery.description.substring(0, 50))}...`
          : escapeHtml(gallery.description) || 'N/A'
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (gallery) => (
        <div className="action-buttons">
          <button onClick={() => handleView(gallery)} className="btn-sm" title="View">
            <Eye size={16} />
          </button>
          <button onClick={() => handleEdit(gallery)} className="btn-edit" title="Edit">
            <Edit size={16} />
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => handleDelete(gallery.id)} className="btn-delete" title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="gallery-management">
      <div className="page-header">
        <h2><Image size={24} /> Gallery Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add Gallery Item
        </motion.button>
      </div>

      <DataTable
        data={galleries}
        columns={columns}
        loading={loading}
        emptyMessage="No gallery items found"
      />

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingGallery(null);
          setFormData({ title: '', description: '', category: '', image: null });
        }}
        title={`${editingGallery ? 'Edit' : 'Add'} Gallery Item`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: sanitizeInput(e.target.value) })}
              maxLength="200"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: sanitizeInput(e.target.value) })}
              rows="3"
              maxLength="1000"
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: sanitizeInput(e.target.value) })}
              maxLength="100"
            />
          </div>
          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingGallery(null);
                setFormData({ title: '', description: '', category: '', image: null });
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingGallery ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Gallery Item Details"
        size="lg"
      >
        {selectedGallery && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                <Image size={32} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedGallery.title}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>{selectedGallery.category || 'Gallery Item'}</p>
            </div>

            {/* Image Section */}
            {selectedGallery.image_url && (
              <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                <img
                  src={selectedGallery.image_url}
                  alt={selectedGallery.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>
            )}

            {/* Description Section */}
            {selectedGallery.description && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Description</h4>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ margin: '0', color: '#6b7280' }}>{selectedGallery.description}</p>
                </div>
              </div>
            )}

            {/* Meta Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Category</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedGallery.category || 'N/A'}</div>
                </div>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Created</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedGallery.created_at ? new Date(selectedGallery.created_at).toLocaleDateString() : 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Gallery;